// @ts-ignore
import { html, TemplateResult, CSSResultGroup } from 'lit';
// @ts-ignore
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './outline-video-vimeo-alt.css.lit';
import { OutlineElement } from '../outline-element/outline-element';
// @ts-ignore
import Player from '@vimeo/player';

export interface OutlineVideoVimeoInterface extends HTMLElement {
  videoID: string;
}

/**
 * The Outline VideoVimeo component
 *
 * @element outline-video-vimeo
 */
@customElement('outline-video-vimeo-alt')
export class OutlineVideoVimeoAlt extends OutlineElement implements OutlineVideoVimeoInterface
{
  static styles: CSSResultGroup = [componentStyles];

  hash: string;
  url: string;
  title: string;
  duration: number;
  currentProgress: number;
  firedProgressEvent: { [key: string]: boolean } = {
    '10': false,
    '25': false,
    '50': false,
    '75': false,
    '90': false,
  };

  constructor() {
    super();
  }

  @property({ type: String, attribute: 'video-id', reflect: true })
  videoID: string;

  @property({ type: String, attribute: 'video-thumb' })
  videoThumb: string;

  @state()
  videoThumbState: boolean;

  player: Player;

  @property({ type: Number, reflect: true, attribute: 'video-autoplay' })
  autoplay = 0;

  @property({ type: Number, reflect: true, attribute: 'video-mute' })
  mute = 0;

  @property({ type: Number, reflect: true, attribute: 'play-in-place' })
  playInPlace = 1;

  @property({ type: String, reflect: true, attribute: 'video-url' })
  videoUrl = '#';

  @property({ type: String, reflect: true, attribute: 'custom-class' })
  customClass = '';

  @property({ type: String, attribute: 'video-description' })
  videoDescription: string;

  @property({ type: String, reflect: true, attribute: 'position' })
  position: string = 'center'; // 'top', 'center' or 'bottom'

  formatEventName(eventName: string) {
    if (eventName === 'ended') {
      return 'Complete';
    } else {
      return eventName.charAt(0).toUpperCase() + eventName.slice(1);
    }
  }

  getProgress(data: { percent: number }) {
    return Math.trunc(data.percent * 100);
  }

  needProgressEvent(data: { percent: number }) {
    const currentProgress = this.getProgress(data);
    const progressValueToSend: string | undefined = Object.keys(
      this.firedProgressEvent
    ).find(key => this.firedProgressEvent[key] === false);
    let progressValueToSendNum: number | null = null;

    if (!progressValueToSend) {
      return false;
    } else {
      progressValueToSendNum = parseInt(progressValueToSend);
    }

    if (currentProgress >= progressValueToSendNum) {
      this.firedProgressEvent[progressValueToSend] = true;
      return progressValueToSend;
    }

    return false;
  }

  fireEvent(eventName: string, data: { percent: number }) {
    // Catch to prevent pause event logging when video stops.
    if (eventName === 'pause' && data.percent === 1) {
      return;
    }

    const needToSendProgressValue =
      eventName === 'progress' ? this.needProgressEvent(data) : true;

    const dataLayerData = {
      videoTitle: this.title,
      videoSource: this.url,
      videoAction: this.formatEventName(eventName),
      videoDuration: this.duration,
      videoCurrentProgress: this.currentProgress,
      Progress:
        eventName === 'progress'
          ? needToSendProgressValue
          : this.getProgress(data),
    };

    if (eventName !== 'progress' || needToSendProgressValue) {
      window.dispatchEvent(
        new CustomEvent('vimeo-video-gtm-event', {
          detail: {
            dataLayerData,
          },
        })
      );
    }
  }

  firstUpdated() {
    this.url = `https://player.vimeo.com/video/${this.videoID}`;
    const iframe = this.shadowRoot?.querySelector('iframe');
    if (iframe) {
      this.player = new Player(iframe);

      this.player.on('play', (data: { percent: number }) =>
        this.fireEvent('play', data)
      );
      this.player.on('pause', (data: { percent: number }) =>
        this.fireEvent('pause', data)
      );
      this.player.on('ended', (data: { percent: number }) =>
        this.fireEvent('ended', data)
      );
      this.player.on('seeked', (data: { percent: number }) =>
        this.fireEvent('seeked', data)
      );
      this.player.on('timeupdate', () =>
        this.player
          .getCurrentTime()
          .then((seconds: number) => (this.currentProgress = seconds))
      );

      this.player.getVideoTitle().then((title: string) => (this.title = title));
      this.player
        .getDuration()
        .then((duration: number) => (this.duration = duration));
    }
  }

  playVideo() {
    this.videoThumbState = !this.videoThumbState;
    this.player.play();
  }

  stopVideo() {
    this.videoThumbState = !this.videoThumbState;
    this.player.pause();
    this.player.setCurrentTime(0);
  }

  render() {
    return html`
      <div
        class="overlay"
        style="background-image: url('${this.videoThumb}'); display: ${this.videoThumbState ? 'none' : 'block'};"
      >
        <div class="overlay-content  ${this.position}">
          <button @click="${this.playVideo}">
            <idb-icon icon="play-circle" size="2em" color="currentColor" stroke-width="2"></idb-icon>
          </button>
          <div class="overlay-text">${this.videoDescription}</div>
        </div>
      </div>
      <div class="video-container ${this.videoThumbState ? 'active' : ''}">
        <div class="close-button" @click="${this.stopVideo}">
          <idb-icon icon="x-circle" size="2em" color="white" stroke-width="2"></idb-icon>
        </div>
        <div class="video-player">
          <iframe
            width="480"
            height="360"
            frameborder="0"
            allowfullscreen="true"
            src = "https://player.vimeo.com/video/${
              this.videoID
            }?api=1&byline=0&portrait=0&title=0&transparent=0&autoplay=${
              this.autoplay
            }&muted=${this.autoplay || this.mute}";
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          >
          </iframe>
        </div>
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-video-vimeo-alt': OutlineVideoVimeoAlt;
  }
}
